import { EcommerceAPI } from "../Client";
import Cookies from "js-cookie";
import jwtDecode from "jwt-decode";

export const WHATSAPP_TEMPLATE_CACHE_MS = 60 * 60 * 1000;
const retryDelay = 5 * 60 * 1000;
const cachePrefix = "whatsapp-templates:v1:";
const entries = new Map();

// Cache only response data, never Axios configs or credentials. Match the API's tenant scope.
const cacheKey = () => {
  let identity = {};
  try {
    const token = sessionStorage.getItem("token");
    if (token) identity = jwtDecode(token);
  } catch { /* Storage can be unavailable or the session may have expired. */ }
  return cachePrefix + JSON.stringify([
    EcommerceAPI.defaults?.baseURL || process.env.REACT_APP_BASEURL_VARIABLE_ECOMMERCE,
    Cookies.get("custom_tenant") || EcommerceAPI.defaults?.headers?.["X-Tenant-Id"] || identity.subDomain,
    identity.subDomain, identity.companyId, identity.sub, identity.userId,
  ]);
};

const templateList = (data) => {
  const value = data?.response ?? data;
  return Array.isArray(value) ? value : value?.data ?? value?.content ?? value?.templates;
};

const readEntry = (key) => {
  if (entries.has(key)) return entries.get(key);
  let entry = {};
  try {
    const stored = JSON.parse(sessionStorage.getItem(key));
    if (stored && Number.isFinite(stored.expiresAt) && Array.isArray(templateList(stored.data)) && stored.data?.success !== false) {
      entry = stored;
    }
  } catch { /* An in-memory cache still works when browser storage is blocked. */ }
  entries.set(key, entry);
  return entry;
};

export const clearWhatsappTemplateCache = () => {
  const key = cacheKey();
  entries.delete(key);
  try { sessionStorage.removeItem(key); } catch { /* Storage is optional. */ }
};

const cachedResponse = (entry) => ({
  data: entry.data,
  templateCache: {
    expiresAt: entry.expiresAt, retryAt: entry.retryAt,
    refreshError: entry.error?.response?.data?.message || entry.error?.message || "",
  },
});

export const getWhatsappTemplatesApi = ({ forceRefresh = false } = {}) => {
  const key = cacheKey();
  const entry = readEntry(key);
  if (entry.inFlight) return entry.inFlight;
  if (!forceRefresh && !entry.error && entry.data !== undefined && Date.now() < entry.expiresAt) {
    return Promise.resolve(cachedResponse(entry));
  }
  if (!forceRefresh && Date.now() < entry.retryAt) {
    return entry.data !== undefined ? Promise.resolve(cachedResponse(entry)) : Promise.reject(entry.error);
  }
  entry.inFlight = (async () => {
    try {
      const response = await EcommerceAPI.get("/admin/whatsapp/templates");
      if (response?.data?.success === false || response?.status >= 400 || !Array.isArray(templateList(response?.data))) {
        throw new Error(response?.data?.message || "The server returned an invalid template list.");
      }
      entry.data = response.data;
      entry.expiresAt = Date.now() + WHATSAPP_TEMPLATE_CACHE_MS;
      entry.error = null; entry.retryAt = null;
      // A mutation can invalidate this entry while its old fetch is still pending.
      if (entries.get(key) === entry) {
        try { sessionStorage.setItem(key, JSON.stringify({ data: entry.data, expiresAt: entry.expiresAt })); }
        catch { /* Keep using the in-memory cache. */ }
      }
      return cachedResponse(entry);
    } catch (error) {
      if ([401, 403].includes(error?.response?.status)) {
        entries.delete(key);
        try { sessionStorage.removeItem(key); } catch { /* Storage is optional. */ }
        throw error;
      }
      entry.error = error;
      entry.retryAt = Date.now() + retryDelay;
      if (entry.data !== undefined) return cachedResponse(entry);
      throw error;
    } finally {
      entry.inFlight = null;
    }
  })();
  return entry.inFlight;
};

const templateUrl = (templateId) =>
  `/admin/whatsapp/templates/${encodeURIComponent(templateId)}`;

export const getWhatsappTemplateApi = (templateId) =>
  EcommerceAPI.get(templateUrl(templateId));

const invalidateAfter = async (request) => {
  const key = cacheKey();
  const response = await request;
  if (response?.data?.success !== false) {
    entries.delete(key);
    try { sessionStorage.removeItem(key); } catch { /* Storage is optional. */ }
  }
  return response;
};

export const updateWhatsappTemplateApi = (templateId, body) =>
  invalidateAfter(EcommerceAPI.put(templateUrl(templateId), body));

export const deleteWhatsappTemplateApi = (templateId) =>
  invalidateAfter(EcommerceAPI.delete(templateUrl(templateId)));

export const createWhatsappTemplateApi = (body) =>
  invalidateAfter(EcommerceAPI.post("/admin/whatsapp/templates", body));

export const sendWhatsappTemplateApi = (body) =>
  EcommerceAPI.post("/admin/whatsapp/templates/send", body);

export const syncWhatsappTemplatesApi = () =>
  EcommerceAPI.post("/admin/whatsapp/templates/sync");

export const getWhatsappTemplateSyncStatusApi = () =>
  EcommerceAPI.get("/admin/whatsapp/templates/sync");
